import { createContext, use, useSyncExternalStore } from "react";
import type { JSX, ReactNode } from "react";
import type { AppUpdateController, UpdateState } from "./AppUpdater";

const AppUpdateContext = createContext<AppUpdateController | null>(null);

/** Properties of {@link AppUpdateProvider}. */
export interface AppUpdateProviderProps {
  readonly controller: AppUpdateController;
  readonly children: ReactNode;
}

/**
 * Provides the application update controller.
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function AppUpdateProvider(props: AppUpdateProviderProps): JSX.Element {
  const provider = <AppUpdateContext value={props.controller}>{props.children}</AppUpdateContext>;

  return provider;
}

/** Update state and action for components. */
export interface AppUpdate {
  readonly state: UpdateState;
  readonly activate: () => void;
}

/**
 * Returns the update state and re-renders on changes.
 *
 * @returns State and activation action.
 * @throws Error if no {@link AppUpdateProvider} is present.
 */
export function useAppUpdate(): AppUpdate {
  const controller = use(AppUpdateContext);

  if (controller === null) {
    throw new Error("useAppUpdate must be used inside an AppUpdateProvider.");
  }

  const state = useSyncExternalStore(controller.subscribe, controller.getState);
  const update: AppUpdate = { state, activate: controller.activate };

  return update;
}
